import { SBEFCustomGroup } from '@shared/construction/sbef-custom-group.model';
import { SbefGroupCode, sbefGroupIds } from '@shared/construction/sbef.model';

/**
 * Ids of the app-wide custom SBEF groups, in canonical (menu/declaration) order. Declared as a
 * const tuple so the ids can be used both as the {@link SBEFCustomGroup} ids and as a strict
 * literal type ({@link SbefCustomGroupId}).
 */
export const SBEF_CUSTOM_GROUP_IDS = ['A5.1', 'A5.2-5'] as const;
export type SbefCustomGroupId = (typeof SBEF_CUSTOM_GROUP_IDS)[number];

const [MATERIAL_WASTE_ID, CONSTRUCTION_SITE_ID] = SBEF_CUSTOM_GROUP_IDS;

/**
 * Material waste bucket (SBEF code 101), surfaced as its own A5 sub-group.
 */
export const MATERIAL_WASTE_SBEF_GROUP = new SBEFCustomGroup({
  sbefCodes: ['101'],
  id: MATERIAL_WASTE_ID,
  description: 'Material waste',
});

/**
 * Construction-site activities (SBEF codes 102-105) grouped as one A5 sub-group.
 */
export const CONSTRUCTION_SITE_SBEF_GROUP = new SBEFCustomGroup({
  sbefCodes: ['102', '103', '104', '105'],
  id: CONSTRUCTION_SITE_ID,
  description: 'Construction site',
});

/**
 * Default custom SBEF groups applied app-wide (and in compute/migrations). Defined in
 * shared so the result pipeline and migrations can resolve the same group keys the UI uses.
 */
export const DEFAULT_SBEF_CUSTOM_GROUPS: SBEFCustomGroup[] = [
  MATERIAL_WASTE_SBEF_GROUP,
  CONSTRUCTION_SITE_SBEF_GROUP,
];

/**
 * Every key the result scope can be filtered/grouped by, mirroring the element/product list
 * "group by code" grouping exactly: the ten standard SBEF groups (`'0'`–`'9'`), the app-wide
 * custom groups (A5.1 / A5.2-5). General content and 100-series codes fold into group 9
 * ({@link SITE_OPERATIONS_SBEF_GROUP_KEY}). This is the canonical key set used for the spread
 * per-group result breakdown, the scope menu, and the list filters, so they all group and filter
 * identically (and the buckets sum back to the flat total).
 */
export type SbefScopeGroupKey = SbefGroupCode | SbefCustomGroupId;

export const sbefScopeGroupKeys: readonly SbefScopeGroupKey[] = [
  ...sbefGroupIds,
  ...SBEF_CUSTOM_GROUP_IDS,
];
